import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight } from "lucide-react";
import { getUITheme, contrastColor } from "@/lib/uiTheme";
import { iconFor } from "@/lib/icons";

export function InfoPanel({ building, onClose, compact = false, night = false, theme }) {
  const t = theme || getUITheme(night);
  const Icon = building ? iconFor(building.slug) : null;
  return (
    <AnimatePresence mode="wait">
      {building && (
        <motion.aside
          key={building.id || building.slug}
          data-testid={building.isPerson ? "persona-info-panel" : "service-info-panel"}
          initial={{ opacity: 0, x: 32 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 32 }}
          transition={{ duration: 0.38, ease: [0.2, 0.8, 0.2, 1] }}
          className={`z-40 ${
            compact
              ? "absolute left-3 right-3 bottom-24 mx-auto max-w-[460px]"
              : "absolute left-3 right-3 bottom-4 md:left-auto md:right-6 md:top-24 md:bottom-auto w-auto md:w-[400px]"
          }`}
        >
          <div
            className="rounded-xl border overflow-hidden"
            style={{ background: t.infoBg, borderColor: t.panelBorder, boxShadow: "var(--shadow-panel)" }}
          >
            <div style={{ height: 6, background: building.accent || "#FFCE24" }} />
            <div className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <span
                    className="inline-flex h-11 w-11 items-center justify-center rounded-lg border shrink-0"
                    style={{ background: building.color, borderColor: "rgba(0,0,0,0.15)" }}
                    data-testid="service-info-icon"
                  >
                    {Icon && <Icon size={22} color={contrastColor(building.color)} />}
                  </span>
                  <div>
                    <span
                      className="inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-[10px] font-display font-semibold uppercase tracking-[0.14em]"
                      style={{ background: t.categoryPillBg, color: t.categoryPillText }}
                    >
                      <span className="h-1.5 w-1.5 rounded-full" style={{ background: "#FFCE24" }} />
                      {building.category}
                    </span>
                    <h2
                      data-testid="service-info-title"
                      className="mt-2 font-display font-bold uppercase tracking-tight text-3xl md:text-4xl leading-[0.95]"
                      style={{ color: t.textStrong }}
                    >
                      {building.name}
                    </h2>
                  </div>
                </div>
                <button
                  data-testid="service-info-close-button"
                  aria-label="Close"
                  onClick={onClose}
                  className={`shrink-0 rounded-md p-1.5 transition-colors ${t.hoverClass}`}
                  style={{ color: t.textMuted }}
                >
                  <X size={18} />
                </button>
              </div>
              <p className="mt-3 font-body text-sm md:text-[15px] leading-relaxed" style={{ color: t.textBody }}>
                {building.description}
              </p>
              <div className="mt-4 flex items-center gap-2">
                {building.learnMoreUrl ? (
                  <a
                    data-testid="service-info-learn-more-button"
                    href={building.learnMoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-[10px] px-4 py-2.5 text-sm font-display font-semibold uppercase tracking-wide text-[#2B2E34] hover:brightness-95 active:scale-[0.98] transition-[filter]"
                    style={{ background: "#FFCE24" }}
                  >
                    Learn more <ArrowUpRight size={16} />
                  </a>
                ) : null}
                <button
                  onClick={onClose}
                  className={`inline-flex items-center rounded-[10px] border px-4 py-2.5 text-sm font-display font-semibold uppercase tracking-wide transition-colors ${t.hoverClass}`}
                  style={{ borderColor: t.panelBorder, background: t.panelSurface, color: t.textStrong }}
                >
                  Back
                </button>
              </div>
            </div>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}

export default InfoPanel;
